import { useQuery, useMutation, useQueryClient, QueryObserverOptions } from "@tanstack/react-query";
import type { Job, JobListItem } from "@models/Job";
import { apiGet, apiPost, apiPut, apiDelete } from "../client";

interface CommonQueryOptions {
	keepPreviousData?: QueryObserverOptions["keepPreviousData"];
	onSuccess?: QueryObserverOptions["onSuccess"];
	refetchOnWindowFocus?: boolean;
}

/**
 * Hook to get paginated list of jobs with optional filters
 */
export function useJobs(
	page: number,
	pageSize: number,
	status?: Job["status"][],
	customerId?: string,
	group?: boolean,
	endDate?: string,
	onError?: (errorCode: string) => void,
	opts: CommonQueryOptions = {},
) {
	const params: Record<string, any> = {
		pageSize: pageSize.toString(),
		page: page.toString(),
		expand: "customer",
		status: status ? status.join(",") : "",
		customerId: customerId || "",
		group: group ? "true" : "false",
		endDate: endDate || "",
	};

	const { data, ...rest } = useQuery<
		| { totalCount: number; jobs: JobListItem[] }
		| { totalCount: number; onGoing: JobListItem[]; past: JobListItem[]; upComing: JobListItem[] }
	>({
		queryKey: ["jobs", page, status, customerId],
		queryFn: () =>
			apiGet<
				| { totalCount: number; jobs: JobListItem[] }
				| { totalCount: number; onGoing: JobListItem[]; past: JobListItem[]; upComing: JobListItem[] }
			>("/job", params),
		...opts,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		data,
		...rest,
	};
}

/**
 * Hook to get a single job by ID with expanded data
 */
export function useJob(id?: string, onError?: (errorCode: string) => void, opts: CommonQueryOptions = {}) {
	const { data, ...rest } = useQuery<Job>({
		queryKey: ["job", id],
		queryFn: () =>
			apiGet<Job>(`/job/${id}`, {
				expand: "applications,shifts,customer,contract,metadata,invitations",
				include: "applications.consultant.rating,consultantShifts",
			}),
		enabled: !!id,
		...opts,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		job: data,
		...rest,
	};
}

/**
 * Hook to get job applications for a specific job
 */
export function useJobApplications(id?: string, onError?: (errorCode: string) => void, opts: CommonQueryOptions = {}) {
	const { data, ...rest } = useQuery<any>({
		queryKey: ["job-application", id],
		queryFn: () =>
			apiGet<any>(`/job/${id}/application`, {
				expand: "consultant,consultant.hasCustomerExperience,metadata",
				include: "invitations",
			}),
		enabled: !!id,
		...opts,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		...data,
		...rest,
	};
}

/**
 * Hook to load matching consultants for a job draft (with and without customer experience)
 */
export function useLoadMatchingConsultants(onError?: (errorCode: string) => void) {
	return useMutation({
		mutationFn: async (jobDetails: any) => {
			console.log("[useLoadMatchingConsultants] Starting two parallel requests...");

			// Make two parallel requests: one for consultants WITH experience, one WITHOUT
			const [withExperience, withoutExperience] = await Promise.all([
				apiPost<any>("/job/draft/user?hasCustomerExperience=true", jobDetails).then((res) => {
					console.log("[useLoadMatchingConsultants] WITH experience response:", res.totalCount, "consultants");
					return res;
				}),
				apiPost<any>("/job/draft/user?hasCustomerExperience=false", jobDetails).then((res) => {
					console.log(
						"[useLoadMatchingConsultants] WITHOUT experience response:",
						res.totalCount,
						"consultants",
					);
					return res;
				}),
			]);

			// Create a Set to track seen consultant IDs to avoid duplicates
			const seenIds = new Set<string>();

			// Merge consultants: experienced first, then non-experienced
			const mergedUsers: any[] = [];

			// Add consultants with experience first
			for (const user of withExperience.users) {
				if (!seenIds.has(user.id)) {
					seenIds.add(user.id);
					mergedUsers.push(user);
				}
			}

			// Add consultants without experience
			for (const user of withoutExperience.users) {
				if (!seenIds.has(user.id)) {
					seenIds.add(user.id);
					mergedUsers.push(user);
				}
			}

			// Return merged response with combined totalCount
			return {
				totalCount: withExperience.totalCount + withoutExperience.totalCount,
				users: mergedUsers,
			};
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to create a new job
 */
export function useCreateJob(onSuccess?: (data: Job) => any, onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (jobDetails: any) => apiPost<Job>("/job", jobDetails),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
			onSuccess && onSuccess(data);
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to update an existing job
 */
export function useUpdateJob(jobId: string, onSuccess?: () => any, onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: any) => apiPut(`/job/${jobId}`, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["job", jobId] });
			onSuccess && onSuccess();
		},
		onError: (error: any) => {
			console.log({ error });
			const errorCode =
				error?.response?.data?.detail || error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to cancel a job
 */
export function useCancelJob(jobId: string, onSuccess?: () => any, onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body?: any) => apiPost(`/job/${jobId}/cancel`, body ?? {}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
			queryClient.invalidateQueries({ queryKey: ["job", jobId] });
			onSuccess && onSuccess();
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to update a job application
 */
export function useUpdateApplication(onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ jobId, applicationId, body }: { jobId: string; applicationId: string; body: any }) =>
			apiPut(`/job/${jobId}/application/${applicationId}`, body),
		onSuccess: (_, data) => {
			queryClient.invalidateQueries({ queryKey: ["job", data.jobId] });
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to update job shifts
 */
export function useUpdateShifts(onSuccess?: () => any, onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ jobId, shifts }: { jobId: string; shifts: any[] }) =>
			apiPut(
				`/job/${jobId}/shift`,
				shifts.map(({ id, start, end, flexible, break: breakVal }) => ({
					id,
					start,
					end,
					flexible,
					break: breakVal,
				})),
			),
		onSuccess: (_data, { jobId }) => {
			queryClient.invalidateQueries({ queryKey: ["job", jobId] });
			queryClient.invalidateQueries({ queryKey: ["jobs"] });
			onSuccess && onSuccess();
		},
		onError: (error: any) => {
			console.log({ error });
			const errorCode =
				error?.response?.data?.detail || error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to update job application (duplicate of useUpdateApplication for compatibility)
 */
export function useJobApplicationUpdate(onSuccess?: () => any, onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ jobId, applicationId, body }: { jobId: string; applicationId: string; body: any }) =>
			apiPut(`/job/${jobId}/application/${applicationId}`, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["job-application-update"] });
			onSuccess && onSuccess();
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}
